import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { api, formatError, isValidEmail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Compact login panel anchored under the header LOG IN button. Logs in IN PLACE (applyAuth
// refreshes auth state — no navigation/redirect), closes on Esc / outside-click / success,
// and shows failures inline. Uses the same POST /auth/login as the /join login, so it respects
// the beta gate identically. CREATE ACCOUNT still routes to /join.
export default function LoginDropdown({ triggerClassName = "" }) {
  const { applyAuth } = useAuth();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(email)) { setError("Enter a valid email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data.token, data.user); // refresh in place — header re-renders to logged-in
      setOpen(false);
      setEmail(""); setPassword("");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Login failed.");
    } finally { setBusy(false); }
  };

  const lbl = { color: "#8B8D90" };
  const inputCls = "mt-1 w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-2.5 text-[#E8E8EA] placeholder:text-[#6B6D70] font-body text-sm transition-colors";

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="nav-login"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={triggerClassName || "px-3 py-2 text-[12px] font-mono uppercase text-secondary-foreground hover:text-foreground transition-colors whitespace-nowrap"}
      >
        Log in
      </button>
      {open && (
        <div
          data-testid="login-dropdown-panel"
          className="absolute right-0 top-full mt-2 w-80 border p-5 text-left z-[60] shadow-2xl"
          style={{ backgroundColor: "#111213", borderColor: "#2E2F31" }}
        >
          <p className="font-mono text-[10px] tracking-[0.25em]" style={lbl}>// RETURNING OPERATIVE</p>
          <form onSubmit={submit} className="mt-3 space-y-3" data-testid="login-dropdown-form">
            {error && (
              <div data-testid="login-dropdown-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-xs px-3 py-2 font-mono">{error}</div>
            )}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wide" style={lbl}>Email</label>
              <input data-testid="login-dropdown-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className={inputCls} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="font-mono text-[10px] uppercase tracking-wide" style={lbl}>Password</label>
                <Link to="/forgot-password" data-testid="login-dropdown-forgot" onClick={() => setOpen(false)} className="font-mono text-[10px] uppercase text-olive-bright hover:text-volt transition-colors">Forgot?</Link>
              </div>
              <div className="relative mt-1">
                <input data-testid="login-dropdown-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  className="w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-2.5 pr-10 text-[#E8E8EA] placeholder:text-[#6B6D70] font-body text-sm transition-colors" />
                <button type="button" data-testid="login-dropdown-pw-toggle" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 p-1" style={{ color: "#6B6D70" }}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button data-testid="login-dropdown-submit" disabled={busy} className="w-full bg-[#57682A] hover:bg-[#6f7a56] disabled:opacity-50 text-[#E8E8EA] font-heading font-bold uppercase text-sm py-2.5 transition-colors">
              {busy ? "Authenticating..." : "Log In"}
            </button>
          </form>
          <Link to="/join" data-testid="login-dropdown-create" onClick={() => setOpen(false)} className="mt-3 block text-center font-mono text-[11px] uppercase tracking-wide text-secondary-foreground hover:text-foreground transition-colors">
            New here? Create account →
          </Link>
        </div>
      )}
    </div>
  );
}
